import { Form } from "@inertiajs/react"
import { Flag } from "lucide-react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useFirstErrorFocus } from "@/hooks/use-first-error-focus"
import { flagMissionCollabSubmissionPath } from "@/routes"
import type { Submission } from "@/types/submissions"

const FLAG_NOTE_MAX = 2000

/**
 * OKS-35 — Brand-side flag confirm dialog (UX Spec §5).
 *
 * Inertia `<Form>` POST with required `flag_note` (1..2000 chars).
 * Errors render via the render-prop `errors` object — Crew rule (no
 * shadcn Form / RHF / zod). Char counter is `aria-live=polite` for
 * screen readers (UX Spec §9 lock).
 *
 * Idempotent re-fire on already-flagged returns 200 + flash + ignores
 * the incoming note (KD-5 — symmetric with Approve).
 */
interface Props {
  submission: Submission
  missionId: number
  trigger?: React.ReactElement
  /**
   * OKS-708 — optional POST target override. Defaults to the brand-nested
   * `flagMissionCollabSubmissionPath`; the agency cross-brand home passes
   * its server-supplied `/agency/submissions/:id/flag` URL. Backward-
   * compatible.
   */
  action?: string
}

export function FlagSubmissionConfirmDialog({
  submission,
  missionId,
  trigger,
  action,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [noteLength, setNoteLength] = useState(0)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setNoteLength(0)
      }}
    >
      <DialogTrigger
        render={
          trigger ?? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid={`flag-submission-trigger-${submission.id}`}
            >
              <Flag aria-hidden="true" className="mr-1 size-4" />
              {t("submissions.brand.confirm.flag_cta")}
            </Button>
          )
        }
      />
      <DialogContent
        data-testid={`flag-submission-dialog-${submission.id}`}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("submissions.brand.confirm.flag_title")}</DialogTitle>
          <DialogDescription>
            {t("submissions.brand.confirm.flag_body")}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={
            action ??
            flagMissionCollabSubmissionPath(
              missionId,
              submission.collab_id,
              submission.id,
            )
          }
          method="post"
          // OKS-920 — a blank note is a CORRECTABLE failure: type one, retry,
          // it succeeds. So the server ships the error bag on a 302 (see
          // `BrandSubmissionsController#redirect_after_brand_action`), which
          // makes `props.errors` non-empty; Inertia therefore classifies the
          // response as a FAILURE and fires `onError` rather than `onSuccess`.
          // The dialog stays mounted and open, the render-prop `errors` below
          // populate, and the typed note survives. There is no toast on this
          // path — an inline message plus a toast for the same failure is
          // double-reporting (UX Spec §9, "never both").
          //
          // `onSuccess` is therefore reached ONLY on a real success. Note what
          // it used to mean: while #flag suppressed its bag, an empty
          // `props.errors` made Inertia report the FAILED submit as a success,
          // and this very handler closed the dialog. That is what OKS-728 read
          // as "the modal remounts" — it never did (measured, OKS-920 F1).
          // Nothing here needs `preserveState`: a POST already defaults to
          // `preserveState: true` via `@inertiajs/core` `Router#post`.
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing, errors }) => (
            <FlagDialogBody
              submission={submission}
              processing={processing}
              errors={errors}
              noteLength={noteLength}
              onNoteLengthChange={setNoteLength}
              onCancel={() => setOpen(false)}
            />
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}

interface FlagDialogBodyProps {
  submission: Submission
  processing: boolean
  /**
   * Inertia's `<Form>` slot type. `app/frontend/types/globals.d.ts` declares
   * `errorValueType: string[]`, but this endpoint ships a single STRING —
   * `serialise_errors` returns `{flag_note: t(...)}`. The declared type and
   * the wire shape disagree, so `renderableError` below handles both rather
   * than casting one away (same defensive read as
   * `agency/invite-teammate-dialog.tsx`).
   */
  errors: Record<string, string[]>
  noteLength: number
  onNoteLengthChange: (length: number) => void
  onCancel: () => void
}

/**
 * Collapse an Inertia error value to something renderable, or `null` when
 * there is no error to show.
 *
 * ⚠ An empty array is TRUTHY in JS, so a bare `errors.flag_note && …` would
 * mark the field invalid and point `aria-describedby` at an empty message.
 * That is a live hazard given the declared `string[]` type, so emptiness is
 * checked rather than assumed away.
 */
function renderableError(value: string[] | string | undefined): string | null {
  if (value === undefined) return null
  if (Array.isArray(value)) return value.length > 0 ? value.join(" ") : null
  return value === "" ? null : value
}

/**
 * OKS-925 — inner body so `useFirstErrorFocus` can run at component-body
 * scope. The Inertia `<Form>` render-prop is a callback, not a component,
 * so hooks cannot live inside it — the same constraint `MissionFormBody`
 * was extracted for (`missions/mission-form.tsx`).
 *
 * Three a11y affordances the failure path was missing (UX Spec §6). The
 * message already carried `role="alert"`, so a failure WAS announced —
 * what was absent was any link between the announcement and the field:
 *
 *   1. `aria-invalid` — driven off the SERVER error bag, not client state.
 *      This is the difference from the approve dialog, which marks invalid
 *      on a client-side over-limit and ships no bag at all. Flag is the one
 *      surface with a real server error to consult. No styling is added:
 *      `ui/textarea.tsx` already carries `aria-invalid:border-destructive`
 *      and was simply never switched on here.
 *   2. Focus move — `useFirstErrorFocus` resolves `flag_note` by `name` and
 *      focuses the textarea, so a keyboard/SR user lands on the field to
 *      retype instead of being left where they were.
 *   3. `aria-describedby` association — the error is appended to the field's
 *      description ONLY while it renders. Deliberately NOT the approve
 *      dialog's shape, which hardcodes both idrefs and therefore points at a
 *      missing element whenever the error is absent; a dangling idref is
 *      dropped from the computed accessible description, so an attribute
 *      check passes while the AX tree says nothing.
 *
 * Ids stay submission-scoped: the brand and agency home pages render one
 * dialog per pending submission, so a fixed id would collide.
 */
function FlagDialogBody({
  submission,
  processing,
  errors,
  noteLength,
  onNoteLengthChange,
  onCancel,
}: FlagDialogBodyProps) {
  const { t } = useTranslation()
  const bodyRef = useRef<HTMLDivElement>(null)

  // Fires when the `errors` object identity changes. Inertia holds it in
  // `useState`, so that is once per server response — NOT once per render,
  // which is why typing in the field does not re-steal focus mid-correction.
  useFirstErrorFocus(errors, bodyRef)

  const noteId = `flag_note_${submission.id}`
  const helperId = `${noteId}_helper`
  const errorId = `${noteId}_error`
  const noteError = renderableError(errors.flag_note)

  return (
    <div ref={bodyRef} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={noteId}>
          {t("submissions.brand.confirm.flag_note_label")}
          {/* OKS-728 — visual required marker; native `required`
              dropped so a blank submit reaches the server and renders
              the translated inline error instead of a native bubble.
              flag_note is already server-validated (Submissions::Flag
              → flag_note_required) + carries aria-required below. */}
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        </Label>
        <Textarea
          id={noteId}
          name="flag_note"
          maxLength={FLAG_NOTE_MAX}
          rows={6}
          placeholder={t("submissions.brand.confirm.flag_note_placeholder")}
          aria-required="true"
          aria-invalid={noteError ? "true" : undefined}
          aria-describedby={noteError ? `${helperId} ${errorId}` : helperId}
          onChange={(event) => onNoteLengthChange(event.target.value.length)}
          data-testid={`flag-submission-note-${submission.id}`}
        />
        <div
          id={helperId}
          className="text-muted-foreground flex items-center justify-between text-xs"
          aria-live="polite"
        >
          <span>{t("submissions.brand.confirm.flag_note_helper")}</span>
          <span data-testid={`flag-submission-counter-${submission.id}`}>
            {noteLength}/{FLAG_NOTE_MAX}
          </span>
        </div>
        {noteError && (
          <p
            id={errorId}
            role="alert"
            className="text-destructive text-sm"
            data-testid={`flag-submission-error-${submission.id}`}
          >
            {noteError}
          </p>
        )}
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          data-testid={`flag-submission-cancel-${submission.id}`}
        >
          {t("submissions.brand.confirm.cancel")}
        </Button>
        <Button
          type="submit"
          variant="destructive"
          disabled={processing}
          data-testid={`flag-submission-confirm-${submission.id}`}
        >
          {t("submissions.brand.confirm.flag_cta")}
        </Button>
      </DialogFooter>
    </div>
  )
}
